/**
 * The group view of a tenant (FR-PIL-09, FR-PIL-12, FR-CON-13): the figures of each site of the
 * year, consolidated per company and for the tenant, and the group alerts that point at the sites
 * needing attention. Pure rules over figures the API computes per site.
 */
import { z } from 'zod';
import { hasValidAuthorization, INSTALLATION_AUTHORIZATION_KINDS, } from './expedition.js';
const round3 = (n) => Math.round(n * 1000) / 1000;
const round2 = (n) => Math.round(n * 100) / 100;
const family = () => ({ DD: 0, DND: 0, INERTE: 0 });
const sumOf = (f) => round3(f.DD + f.DND + f.INERTE);
/** GroupTotals of a set of sites; rates are computed from the sums, never averaged. */
export function groupTotals(sites) {
    const generated = family();
    const shipped = family();
    let recoveredT = 0;
    let baseT = 0;
    let expeditions = 0;
    let cost = 0;
    let costSites = 0;
    const openAlerts = { critical: 0, warning: 0, info: 0 };
    let overdueReturns = 0;
    for (const s of sites) {
        for (const k of ['DD', 'DND', 'INERTE']) {
            generated[k] = round3(generated[k] + s.generated[k]);
            shipped[k] = round3(shipped[k] + s.shipped[k]);
        }
        recoveredT = round3(recoveredT + s.valorisation.recoveredT);
        baseT = round3(baseT + s.valorisation.baseT);
        expeditions += s.expeditions;
        if (s.cost !== null) {
            cost = round2(cost + s.cost);
            costSites += 1;
        }
        openAlerts.critical += s.openAlerts.critical;
        openAlerts.warning += s.openAlerts.warning;
        openAlerts.info += s.openAlerts.info;
        overdueReturns += s.overdueReturns;
    }
    const shippedT = sumOf(shipped);
    // the cost per tonne only on the tonnage of the sites whose cost is known
    const costedT = sumOf(sites
        .filter((s) => s.cost !== null)
        .reduce((acc, s) => {
        acc.DD += s.shipped.DD;
        acc.DND += s.shipped.DND;
        acc.INERTE += s.shipped.INERTE;
        return acc;
    }, family()));
    return {
        sites: sites.length,
        generated,
        shipped,
        shippedT,
        valorisation: { recoveredT, baseT, rate: baseT > 0 ? recoveredT / baseT : null },
        expeditions,
        cost: costSites ? cost : null,
        costPartial: costSites > 0 && costSites < sites.length,
        costPerTonne: costSites && costedT > 0 ? Math.round(cost / costedT) : null,
        openAlerts,
        overdueReturns,
    };
}
/** The sites grouped per company with their totals, then the tenant's totals (FR-CON-13). */
export function consolidate(sites) {
    const byCompany = new Map();
    for (const s of sites) {
        const c = byCompany.get(s.companyId) ?? {
            companyId: s.companyId,
            companyName: s.companyName,
            sites: [],
            totals: groupTotals([]),
        };
        c.sites.push(s);
        byCompany.set(s.companyId, c);
    }
    const companies = [...byCompany.values()]
        .map((c) => ({
        ...c,
        sites: [...c.sites].sort((a, b) => a.siteName.localeCompare(b.siteName, 'fr')),
        totals: groupTotals(c.sites),
    }))
        .sort((a, b) => a.companyName.localeCompare(b.companyName, 'fr'));
    return { companies, tenant: groupTotals(sites) };
}
// ---- group alerts (FR-PIL-12) -------------------------------------------------------------------
export const GROUP_ALERT_KINDS = [
    'ANNUAL_REPORT_UNFILED',
    'EXPIRED_AUTHORIZATION_IN_USE',
    'OVERDUE_RETURNS',
];
export const groupSettingsSchema = z.object({
    overdueReturnsThreshold: z.number().int().min(0).max(1000),
    annualReportWarningDays: z.number().int().min(0).max(120),
});
export const DEFAULT_GROUP_SETTINGS = {
    overdueReturnsThreshold: 5,
    annualReportWarningDays: 30,
};
const daysBetween = (from, to) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
/**
 * The sites with an unfiled annual report (late, or due within the warning days), with an
 * authorization lapsed while it is in use, or with more overdue returns than the threshold.
 * Deactivated sites are left out; critical alerts come first.
 */
export function groupAlerts(sites, today, settings = DEFAULT_GROUP_SETTINGS) {
    const alerts = [];
    for (const s of sites.filter((x) => x.active)) {
        const base = { siteId: s.siteId, siteName: s.siteName, companyName: s.companyName };
        const r = s.annualReport;
        if (r.status !== 'FILED') {
            const left = daysBetween(today, r.deadline);
            if (left < 0 || left <= settings.annualReportWarningDays) {
                alerts.push({
                    ...base,
                    kind: 'ANNUAL_REPORT_UNFILED',
                    severity: left < 0 ? 'CRITICAL' : 'WARNING',
                    value: r.year,
                    detail: r.deadline,
                });
            }
        }
        if (s.authorizationGaps.length) {
            alerts.push({
                ...base,
                kind: 'EXPIRED_AUTHORIZATION_IN_USE',
                severity: 'CRITICAL',
                value: s.authorizationGaps.length,
                detail: s.authorizationGaps.map((g) => g.prestataireName).join(', '),
            });
        }
        if (s.overdueReturns > settings.overdueReturnsThreshold) {
            alerts.push({
                ...base,
                kind: 'OVERDUE_RETURNS',
                severity: 'WARNING',
                value: s.overdueReturns,
                detail: null,
            });
        }
    }
    const rank = { CRITICAL: 0, WARNING: 1 };
    return alerts.sort((a, b) => rank[a.severity] - rank[b.severity] ||
        GROUP_ALERT_KINDS.indexOf(a.kind) - GROUP_ALERT_KINDS.indexOf(b.kind) ||
        a.siteName.localeCompare(b.siteName, 'fr'));
}
/**
 * The prestataires whose authorization does not cover a date they are used on: a transporter
 * without a collection-transport authorization (BR-05), a destination without an installation
 * authorization for that installation (BR-08). One gap per prestataire, counting its uses.
 */
export function authorizationGaps(uses, authorizations) {
    const gaps = new Map();
    for (const u of uses) {
        const auths = authorizations.get(u.prestataireId) ?? [];
        const ok = u.role === 'TRANSPORTER'
            ? hasValidAuthorization(auths, u.on, ['COLLECTE_TRANSPORT'])
            : hasValidAuthorization(auths, u.on, INSTALLATION_AUTHORIZATION_KINDS, u.installationId);
        if (ok)
            continue;
        const g = gaps.get(u.prestataireId) ?? {
            prestataireId: u.prestataireId,
            prestataireName: u.prestataireName,
            roles: [],
            expeditions: 0,
            routes: 0,
        };
        if (!g.roles.includes(u.role))
            g.roles.push(u.role);
        if (u.source === 'EXPEDITION')
            g.expeditions += 1;
        else
            g.routes += 1;
        gaps.set(u.prestataireId, g);
    }
    return [...gaps.values()].sort((a, b) => a.prestataireName.localeCompare(b.prestataireName, 'fr'));
}
//# sourceMappingURL=group.js.map